import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ShoppingCart,
  Clock,
  Package,
  Truck,
  CheckCircle,
  XCircle,
  MagnifyingGlass,
  DownloadSimple,
  Eye,
  CurrencyDollar,
  Storefront,
} from '@phosphor-icons/react';
import {
  apiClient,
  Order,
  OrderStatus,
  PaymentStatus,
  FulfillmentStatus,
  API_ENDPOINTS,
  MERCHANT_ROUTES,
  useAuthStore,
} from '@dukene/shared';
import { format } from 'date-fns';

export default function Orders() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'ALL'>('ALL');
  const [paymentFilter, setPaymentFilter] = useState<PaymentStatus | 'ALL'>('ALL');
  const [fulfillmentFilter, setFulfillmentFilter] = useState<FulfillmentStatus | 'ALL'>('ALL');
  const activeStoreId = useAuthStore((state) => state.activeStoreId);

  const { data: orders = [], isLoading, error } = useQuery<Order[]>({
    queryKey: [
      'merchant-orders',
      activeStoreId,
      statusFilter,
      paymentFilter,
      fulfillmentFilter,
      searchQuery,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      if (paymentFilter !== 'ALL') params.append('paymentStatus', paymentFilter);
      if (fulfillmentFilter !== 'ALL') params.append('fulfillmentStatus', fulfillmentFilter);
      if (searchQuery) params.append('search', searchQuery);

      const res = await apiClient.get<any>(`${API_ENDPOINTS.ORDERS.LIST}?${params}`);
      return Array.isArray(res) ? res : res?.data || res?.orders || [];
    },
    enabled: !!activeStoreId,
  });

  const getOrderStatusBadge = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.DELIVERED:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-[#E3F1DF] text-[#006838] border border-[#B3DEC1]">
            <CheckCircle weight="fill" className="w-3 h-3" />
            Delivered
          </span>
        );
      case OrderStatus.SHIPPED:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-[#EBF5FA] text-[#006FBB] border border-[#BDE1F4]">
            <Truck weight="fill" className="w-3 h-3" />
            Shipped
          </span>
        );
      case OrderStatus.PROCESSING:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-200">
            <Package weight="fill" className="w-3 h-3" />
            Processing
          </span>
        );
      case OrderStatus.CANCELLED:
      case OrderStatus.REFUNDED:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
            <XCircle weight="fill" className="w-3 h-3" />
            {status}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            <Clock weight="fill" className="w-3 h-3" />
            {status || 'Pending'}
          </span>
        );
    }
  };

  const getPaymentBadge = (status: PaymentStatus | string) => {
    const s = (status || '').toUpperCase();
    if (s === 'PAID' || s === 'COMPLETED' || s === 'CONFIRMED' || s === 'SUCCESS') {
      return (
        <span className="inline-block bg-[#E3F1DF] text-[#006838] rounded px-2 py-0.5 text-[10px] font-bold" title="Paid">
          PAID
        </span>
      );
    }
    if (s === 'PENDING') {
      return (
        <span className="inline-block bg-amber-50 text-amber-800 border border-amber-200 rounded px-2 py-0.5 text-[10px] font-bold" title="Payment Pending">
          PENDING
        </span>
      );
    }
    return (
      <span className="inline-block bg-red-50 text-red-700 border border-red-200 rounded px-2 py-0.5 text-[10px] font-bold">
        {s || 'UNPAID'}
      </span>
    );
  };

  const getFulfillmentBadge = (status: FulfillmentStatus) => {
    const s = (status || '').toUpperCase();
    return (
      <span className="inline-block bg-[#F6F6F7] dark:bg-slate-800 text-[#5C5F62] dark:text-slate-300 border border-[#D2D5D9] dark:border-slate-700 rounded px-2 py-0.5 text-[10px] font-semibold">
        {s.replace('_', ' ')}
      </span>
    );
  };

  const exportOrdersCSV = () => {
    if (!orders || orders.length === 0) return;
    const headers = ['Order Number', 'Customer', 'Sales Channel', 'Date', 'Payment Status', 'Payment Method', 'Confirmation Ref', 'Status', 'Total (KES)'];
    const rows = orders.map((o: any) => [
      o.orderNumber || o.id,
      o.customer ? `${o.customer.firstName || ''} ${o.customer.lastName || ''}`.trim() || (o.source === 'POS' ? 'Walk in' : 'Guest') : (o.source === 'POS' ? 'Walk in' : 'Guest'),
      o.salesChannel || 'Online Storefront',
      o.createdAt ? format(new Date(o.createdAt), 'yyyy-MM-dd HH:mm') : '',
      String(o.paymentStatus || ''),
      String(o.paymentMethod || 'M-Pesa Checkout'),
      String(o.paymentDetails?.confirmationCode || ''),
      String(o.status || ''),
      Number(o.total || 0),
    ]);
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csvContent));
    link.setAttribute('download', `orders-export-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const stats = {
    total: orders.length,
    pending: orders.filter((o) => o.status === OrderStatus.PENDING).length,
    delivered: orders.filter((o) => o.status === OrderStatus.DELIVERED).length,
    revenue: orders.reduce((sum, o) => {
      const s = String(o.paymentStatus || '').toUpperCase();
      return sum + (
        s === 'PAID' || s === 'COMPLETED' || s === 'CONFIRMED' || s === 'SUCCESS'
          ? Number(o.total || 0)
          : 0
      );
    }, 0),
  };

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-xl p-4 text-red-800 dark:text-red-300 text-xs font-semibold">
        Could not retrieve customer orders. Please reload the page.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* High-density Polaris Header */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold text-[#202223] dark:text-white">
            Customer Orders
          </h1>
        </div>
        <button
          type="button"
          onClick={exportOrdersCSV}
          disabled={orders.length === 0}
          className="h-8 px-3.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-[#F6F6F7] text-xs font-semibold text-[#202223] dark:text-white inline-flex items-center gap-1.5 transition-colors disabled:opacity-50 shrink-0"
        >
          <DownloadSimple weight="bold" className="w-3.5 h-3.5 text-[#006838]" />
          Export CSV
        </button>
      </div>

      {/* KPI Stat Tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">Total Orders</span>
            <ShoppingCart weight="fill" className="w-4 h-4 text-[#5C5F62]" />
          </div>
          <p className="text-xl font-bold text-[#202223] dark:text-white font-mono mt-1">
            {stats.total.toLocaleString()}
          </p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">Pending Review</span>
            <Clock weight="fill" className="w-4 h-4 text-amber-600" />
          </div>
          <p className="text-xl font-bold text-amber-600 font-mono mt-1">
            {stats.pending.toLocaleString()}
          </p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">Delivered</span>
            <CheckCircle weight="fill" className="w-4 h-4 text-[#006838]" />
          </div>
          <p className="text-xl font-bold text-[#006838] font-mono mt-1">
            {stats.delivered.toLocaleString()}
          </p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[#5C5F62] dark:text-slate-400">Paid Revenue</span>
            <CurrencyDollar weight="fill" className="w-4 h-4 text-[#006838]" />
          </div>
          <p className="text-xl font-bold text-[#006838] font-mono mt-1">
            KES {stats.revenue.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Filter Toolbar & Orders Directory */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
        <div className="p-3.5 border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/50 dark:bg-slate-900/40 grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="relative">
            <MagnifyingGlass
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F62]"
            />
            <input
              type="text"
              placeholder="Search order # or customer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-8 pl-9 pr-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white placeholder-[#8C9196] focus:outline-none focus:ring-1 focus:ring-[#006838]"
            />
          </div>

          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as OrderStatus | 'ALL')}
              className="h-8 w-full rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
            >
              <option value="ALL">All Order Statuses</option>
              <option value={OrderStatus.PENDING}>Pending</option>
              <option value={OrderStatus.PROCESSING}>Processing</option>
              <option value={OrderStatus.SHIPPED}>Shipped</option>
              <option value={OrderStatus.DELIVERED}>Delivered</option>
              <option value={OrderStatus.CANCELLED}>Cancelled</option>
            </select>
          </div>

          <div>
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value as PaymentStatus | 'ALL')}
              className="h-8 w-full rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
            >
              <option value="ALL">All Payment States</option>
              <option value={PaymentStatus.PAID}>Paid</option>
              <option value={PaymentStatus.PENDING}>Pending</option>
              <option value={PaymentStatus.FAILED}>Failed</option>
            </select>
          </div>

          <div>
            <select
              value={fulfillmentFilter}
              onChange={(e) => setFulfillmentFilter(e.target.value as FulfillmentStatus | 'ALL')}
              className="h-8 w-full rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
            >
              <option value="ALL">All Fulfillment States</option>
              <option value={FulfillmentStatus.UNFULFILLED}>Unfulfilled</option>
              <option value={FulfillmentStatus.PARTIALLY_FULFILLED}>Partially Fulfilled</option>
              <option value={FulfillmentStatus.FULFILLED}>Fulfilled</option>
            </select>
          </div>
        </div>

        {/* Directory Table */}
        {isLoading ? (
          <div className="p-12 space-y-3 animate-pulse">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-10 bg-[#F6F6F7] dark:bg-slate-900 rounded-lg" />
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16 px-6">
            <ShoppingCart weight="fill" className="w-10 h-10 text-[#8C9196] mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-[#202223] dark:text-white">
              No Orders Displayed
            </h3>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1 max-w-sm mx-auto">
              {searchQuery || statusFilter !== 'ALL' || paymentFilter !== 'ALL'
                ? 'Try clearing your active search filters to view your entire order history.'
                : 'Customer checkout orders from your sales channels will automatically appear here.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7]/60 dark:bg-slate-900/40 text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
                  <th className="py-2.5 px-4">Order</th>
                  <th className="py-2.5 px-4">Customer</th>
                  <th className="py-2.5 px-4">Channel</th>
                  <th className="py-2.5 px-4">Date</th>
                  <th className="py-2.5 px-4">Payment</th>
                  <th className="py-2.5 px-4">Fulfillment</th>
                  <th className="py-2.5 px-4">Status</th>
                  <th className="py-2.5 px-4 text-right">Total</th>
                  <th className="py-2.5 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E1E4E8] dark:divide-slate-800 text-xs">
                {orders.map((order) => {
                  const first = order.customer?.firstName || '';
                  const last = order.customer?.lastName || '';
                  const fullName = `${first} ${last}`.trim();
                  const customerName =
                    fullName ||
                    order.customer?.name ||
                    (order.source === 'POS' ? 'Walk in' : order.email || 'Guest Customer');
                  const salesChannel = (order as any).salesChannel || 'Online Storefront';

                  return (
                    <tr
                      key={order.id}
                      className="hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="py-3 px-4 font-mono font-bold text-[#202223] dark:text-white">
                        <Link
                          to={`${MERCHANT_ROUTES.ORDERS}/${order.id}`}
                          className="hover:text-[#006838] transition-colors"
                        >
                          {order.orderNumber || `#${order.id.slice(0, 8)}`}
                        </Link>
                      </td>

                      <td className="py-3 px-4 font-medium text-[#202223] dark:text-slate-200">
                        {customerName}
                      </td>

                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 bg-[#F0F2F4] dark:bg-slate-800 text-[#202223] dark:text-slate-200 border border-[#D2D5D9] dark:border-slate-700 px-2 py-0.5 rounded text-[10px] font-semibold">
                          <Storefront weight="fill" className="w-3 h-3 text-[#006838]" />
                          {salesChannel}
                        </span>
                      </td>

                      <td className="py-3 px-4 text-[#5C5F62] dark:text-slate-400">
                        {order.createdAt
                          ? format(new Date(order.createdAt), 'MMM d, yyyy HH:mm')
                          : '—'}
                      </td>

                      <td className="py-3 px-4">{getPaymentBadge(order.paymentStatus)}</td>

                      <td className="py-3 px-4">{getFulfillmentBadge(order.fulfillmentStatus)}</td>

                      <td className="py-3 px-4">{getOrderStatusBadge(order.status)}</td>

                      <td className="py-3 px-4 text-right font-mono font-bold text-[#006838]">
                        KES {Number(order.total || 0).toLocaleString()}
                      </td>

                      <td className="py-3 px-4 text-right">
                        <Link
                          to={`${MERCHANT_ROUTES.ORDERS}/${order.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-[#D2D5D9] dark:border-slate-700 hover:bg-[#F6F6F7] dark:hover:bg-slate-800 text-[#5C5F62] dark:text-slate-300 text-xs font-semibold transition-colors"
                        >
                          <Eye weight="bold" className="w-3.5 h-3.5" />
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
